import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Select, TextField, FormControl, InputLabel, MenuItem,
  Grid, Chip, CircularProgress, Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";

const STATUS_OPTIONS = [
  { value: 1, label: "启用" },
  { value: 0, label: "禁用" },
];

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "user_id", label: "金银通ID", alignRight: false },
  { id: "plat_id", label: "鲸世优选UID", alignRight: false },
  { id: "robot_level", label: "机器人等级", alignRight: false },
  { id: "expire_time", label: "到期时间", alignRight: false },
  { id: "buy_stats", label: "抢单成功/失败", alignRight: false },
  { id: "status", label: "状态", alignRight: false },
  { id: "create_time", label: "创建时间", alignRight: false },
  { id: "action", label: "操作", alignRight: false },
];

export default function RobotList() {
  const [filter, setFilter] = useState({
    status: "",
    userId: "",
    platId: "",
    pageNum: 1,
    pageSize: 20,
  });
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = {
        status: filter.status || undefined,
        userId: filter.userId || undefined,
        platId: filter.platId || undefined,
        pageNum: filter.pageNum,
        pageSize: filter.pageSize,
      };
      const res = await asyncRequest({ path: "/jingxi/game/robots/list", data });
      if (res.code === 1) {
        setList(res.data || []);
        setCount(res.count || 0);
      } else {
        alert(res.msg || "获取列表失败");
      }
    } catch (err) {
      console.error(err);
      alert("获取列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleDelete = async (id) => {
    Confirm({
      message: "确认删除该机器人配置？",
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/robots/delete",
            data: { id },
          });
          if (res.code === 1) {
            alert("删除成功");
            fetchList();
          } else {
            alert(res.msg || "删除失败");
          }
        } catch (err) {
          alert("删除失败");
        }
      },
    });
  };

  const handleToggleStatus = async (row) => {
    const newStatus = row.status === 1 ? 0 : 1;
    try {
      const res = await asyncRequest({
        path: "/jingxi/game/robots/update",
        data: { id: row.id, status: newStatus },
      });
      if (res.code === 1) {
        alert("状态更新成功");
        fetchList();
      } else {
        alert(res.msg || "状态更新失败");
      }
    } catch (err) {
      alert("状态更新失败");
    }
  };

  const renderStatus = (status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? (
      <Chip label={opt.label} color={status === 1 ? "success" : "default"} size="small" />
    ) : (
      "-"
    );
  };

  const formatExpireTime = (timestamp) => {
    if (!timestamp) return "-";
    const d = new Date(timestamp * 1000);
    return d.toLocaleString("zh-CN", { hour12: false });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("zh-CN", { hour12: false });
  };

  return (
    <Page title="机器人配置">
      <Container maxWidth="2000px">
        <Card sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h4">机器人配置</Typography>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={4}>
              <TextField
                fullWidth
                size="small"
                label="金银通ID"
                value={filter.userId}
                onChange={(e) => setFilter({ ...filter, userId: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <TextField
                fullWidth
                size="small"
                label="鲸世优选UID"
                value={filter.platId}
                onChange={(e) => setFilter({ ...filter, platId: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>状态</InputLabel>
                <Select
                  value={filter.status}
                  label="状态"
                  onChange={(e) => {
                    setFilter({ ...filter, status: e.target.value, pageNum: 1 });
                    setTimeout(() => fetchList(), 0);
                  }}
                >
                  <MenuItem value="">全部</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilter({ status: "", userId: "", platId: "", pageNum: 1, pageSize: 20 });
              }}
            >
              重置
            </Button>
            <Button variant="contained" size="small" onClick={fetchList} sx={{ ml: 1 }}>
              搜索
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : list.length === 0 ? (
            <SearchNotFound searchQuery={filter} />
          ) : (
            <>
              <Scrollbar>
                <TableContainer>
                  <Table>
                    <ListHead headLabel={TABLE_HEAD} />
                    <TableBody>
                      {list.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.user_id}</TableCell>
                          <TableCell>{row.plat_id || "-"}</TableCell>
                          <TableCell>{row.robot_level || "-"}</TableCell>
                          <TableCell>{formatExpireTime(row.expire_time)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Chip label={row.buy_success_count || 0} color="success" size="small" />
                              <Typography variant="body2" sx={{ lineHeight: "24px" }}>/</Typography>
                              <Chip label={row.buy_fail_count || 0} color="error" size="small" />
                            </Stack>
                          </TableCell>
                          <TableCell>{renderStatus(row.status)}</TableCell>
                          <TableCell>{formatTime(row.create_time)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant={row.status === 1 ? "outlined" : "contained"}
                                color={row.status === 1 ? "warning" : "success"}
                                onClick={() => handleToggleStatus(row)}
                              >
                                {row.status === 1 ? "禁用" : "启用"}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleDelete(row.id)}
                              >
                                删除
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <MyPagination
                count={count}
                rowsPerPage={filter.pageSize}
                page={filter.pageNum}
                onPageChange={(val) => setFilter({ ...filter, pageNum: val })}
                onRowsPerPageChange={(val) => setFilter({ ...filter, pageSize: val, pageNum: 1 })}
              />
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
